import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as FormikForm, Formik } from 'formik';
import { Divider, Grid, Typography } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';

import {
  LeaderboardConfigInput,
  LeaderboardConfig,
  useCreateUpdateLeaderboardConfigMutation,
  BootstrapDocument,
} from 'api';
import { Queries, PaddedPaper } from 'components';
import { LeaderboardSchema } from 'features/admin/validations';
import { useCurrentUser, useNotify } from 'hooks';
import { withSegments } from 'providers/queries';

import { Create, Leaderboard } from '../components/Leaderboards';

interface IPropsEnhanced extends WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    divider: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    help: {
      fontWeight: theme.typography.fontWeightRegular,
      marginBottom: theme.spacing(3),
    },
    customText: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(3),
    },
  });

const enhancers = compose<IPropsEnhanced, {}>(withSegments(), withStyles(styles));

const LeaderboardsIndex: FC<IPropsEnhanced> = ({ classes }) => {
  const [working, setWorking] = useState(false);
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const notify = useNotify();

  const [createUpdateLeaderboardConfig] = useCreateUpdateLeaderboardConfigMutation({
    onError: notify.mutationError,
    onCompleted: editNotify,
  });

  function getEditValues(values: LeaderboardConfigInput) {
    const { id, leaderboard_status, description, segment_ids, start_at, end_at, title } = values;
    return { id, leaderboard_status, description, segment_ids, start_at, end_at, title };
  }

  function editNotify() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
    setWorking(false);
  }

  function handleEdit(config: LeaderboardConfig, values: LeaderboardConfigInput) {
    setWorking(true);

    createUpdateLeaderboardConfig({
      refetchQueries: [{ query: BootstrapDocument, variables: { user_id: currentUser.id } }],
      variables: { data: values },
    });
  }

  function renderConfig(config: LeaderboardConfig) {
    return (
      <FormikForm>
        <Leaderboard config={config} working={working} />
      </FormikForm>
    );
  }

  function renderMutation(configs: LeaderboardConfig[]) {
    return (
      <Grid container direction="column" spacing={3} wrap="nowrap">
        {configs.map(config => (
          <Grid key={config.id} item>
            <Formik
              initialValues={getEditValues(config)}
              onSubmit={values => handleEdit(config, values)}
              validationSchema={LeaderboardSchema(config.all_users)}
              render={() => renderConfig(config)}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Queries.GetLeaderboardConfigs>
      {configs => (
        <Fragment>
          <PaddedPaper>
            <Typography variant="subtitle2">{t('admin:userLeaderboards')}</Typography>
            <Divider className={classes.divider} />
            <Typography className={classes.help} variant="subtitle2" paragraph>
              {t('admin:leaderboardsHelp')}
            </Typography>
            <Create />
          </PaddedPaper>
          <Typography variant="subtitle2" className={classes.customText}>
            {t('common:leaderboard_plural')}
          </Typography>
          {renderMutation(configs)}
        </Fragment>
      )}
    </Queries.GetLeaderboardConfigs>
  );
};

export default enhancers(LeaderboardsIndex);
